import { lazy, FC, Suspense, useState, useEffect } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { MasterLayout } from "../../_jutemplate/layout/MasterLayout";
import TopBarProgress from "react-topbar-progress-indicator";
import { getCSSVariableValue } from "../../_jutemplate/assets/ts/_utils";

// redux
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "../../setup";

// Permission system
import { ProtectedRoute, getRoutePermission } from "../permissions";

// page
import { DashBoard } from "../pages/NewDashboard";

const ProfessionRootPage = lazy(
  () => import("../modules/profession/pages/ProfessionRootPage"),
);

const FAQRootPage = lazy(() => import("../modules/faq/pages/FAQRootPage"));

const DistrictRootPage = lazy(
  () => import("../modules/district/pages/DistrictRootPage"),
);
const DivisionRootPage = lazy(
  () => import("../modules/division/pages/DivisionRootPage"),
);
const ReferenceRootPage = lazy(
  () => import("../modules/reference/pages/ReferenceRootPage"),
);
const GalleryRootPage = lazy(
  () => import("../modules/gallery/pages/GalleryRootPage"),
);
const CropCategoryRootPage = lazy(
  () => import("../modules/crop-category/pages/CropCategoryRootPage"),
);
const CropRootPage = lazy(() => import("../modules/crop/pages/CropRootPage"));

const FarmerSafetyRootPage = lazy(
  () => import("../modules/farmer-safety/pages/FarmerSafetyRootPage"),
);

const TeamConsultantRootPage = lazy(
  () => import("../modules/team-consultant/pages/TeamConsultantRootPage"),
);

const GenericGroupRootPage = lazy(
  () => import("../modules/generic-group/pages/GenericGroupRootPage"),
);

const ProductRootPage = lazy(
  () => import("../modules/product/pages/ProductRootPage"),
);

const PendingProductsRootPage = lazy(
  () => import("../modules/pending-products/pages/PendingProductsRootPage"),
);

const ProductCategoryRootPage = lazy(
  () => import("../modules/product-category/pages/ProductCategoryRootPage"),
);

const ProductSubCategoryRootPage = lazy(
  () =>
    import("../modules/product-sub-category/pages/ProductSubCategoryRootPage"),
);

const ProductCompanyRootPage = lazy(
  () => import("../modules/product-company/pages/ProductCompanyRootPage"),
);

const ProductElementRootPage = lazy(
  () => import("../modules/product-element/pages/ProductElementRootPage"),
);

const ProductSolutionRootPages = lazy(
  () => import("../modules/product-solution/pages/ProductSolutionRootPages"),
);

const ProductSizeRootPage = lazy(
  () => import("../modules/product-size/pages/ProductSizeRootPage"),
);

const ProductUnitRootPage = lazy(
  () => import("../modules/product-unit/pages/ProductUnitTypeRootPage"),
);

const ColorCodeRootPage = lazy(
  () => import("../modules/color-code/pages/ColorCodeRootPage"),
);

const DiseaseInsectRootPage = lazy(
  () => import("../modules/disease-insect/pages/DiseaseInsectRootPage"),
);

const WorkingMoodRootPage = lazy(
  () => import("../modules/working-mood/pages/WorkingMoodRootPage"),
);

const EarlyWarningCategoryRootPage = lazy(
  () =>
    import(
      "../modules/early-warning-category/pages/EarlyWarningCategoryRootPage"
    ),
);

const EarlyWarningRootPage = lazy(
  () => import("../modules/early-warning/pages/EarlyWarningRootPage"),
);

const OrderRootPage = lazy(
  () => import("../modules/order/pages/OrderRootPage"),
);

// const PendingOrderRootPage = lazy(
//   () => import("../modules/order/pages/pending-orders/PendingOrderRootPage")
// );

const StockRootPage = lazy(
  () => import("../modules/inventory/pages/StockRootPage"),
);

const DiseaseDetectionRootPage = lazy(
  () => import("../modules/disease-detection/pages/DiseaseDetectionRootPage"),
);

const CourseRootPage = lazy(
  () => import("../modules/course/pages/CourseRootPage"),
);

const CourseCategoryRootPage = lazy(
  () => import("../modules/course-category/pages/CourseCategoryRootPage"),
);

const DealerRootPage = lazy(
  () => import("../modules/dealer/pages/DealerRootPage"),
);

const PendingDealerRootPage = lazy(
  () => import("../modules/pending-dealer/pages/PendingDealerRootPage"),
);

const PendingPaymentMethodRootPage = lazy(
  () =>
    import(
      "../modules/pending-payment-method/pages/PendingPaymentMethodRootPage"
    ),
);

const ApprovedPaymentMethodRootPage = lazy(
  () =>
    import(
      "../modules/approved-payment-method/pages/ApprovedPaymentMethodRootPage"
    ),
);

const BannerRootPage = lazy(
  () => import("../modules/banner/pages/BannerRootPage"),
);

const FeatureRootPage = lazy(
  () => import("../modules/feature/pages/FeatureRootPage"),
);

const ChadBaganTipsRootPage = lazy(
  () => import("../modules/chad-bagan-tips/pages/ChadBaganTipsRootPage"),
);

const AccountLeadRootPage = lazy(
  () => import("../modules/dealer-account-lead/pages/AccountLeadRootPage"),
);

const PrivateRoutes = () => {
  const [islogin, setIslogin] = useState<any>({});
  const isAuthorized: any = useSelector<RootState>(
    ({ auth }) => auth.user,
    shallowEqual,
  );

  // Helper to wrap route element with protection
  const protectRoute = (path: string, element: React.ReactNode) => {
    const routePermission = getRoutePermission(path);
    return (
      <ProtectedRoute
        requiredPermissions={routePermission?.requiredPermissions || []}
      >
        {element}
      </ProtectedRoute>
    );
  };

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        <Route path="auth/*" element={<Navigate to="/dashboard" />} />

        <Route path="dashboard" element={<DashBoard />} />

        <Route
          path="/profession/*"
          element={protectRoute(
            "/profession",
            <SuspensedView>
              <ProfessionRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/faq/*"
          element={protectRoute(
            "/faq",
            <SuspensedView>
              <FAQRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/district/*"
          element={protectRoute(
            "/district",
            <SuspensedView>
              <DistrictRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/division/*"
          element={protectRoute(
            "/division",
            <SuspensedView>
              <DivisionRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/reference/*"
          element={protectRoute(
            "/reference",
            <SuspensedView>
              <ReferenceRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/gallery/*"
          element={protectRoute(
            "/gallery",
            <SuspensedView>
              <GalleryRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/crop-category/*"
          element={protectRoute(
            "/crop-category",
            <SuspensedView>
              <CropCategoryRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/all-crop/*"
          element={protectRoute(
            "/all-crop",
            <SuspensedView>
              <CropRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/farmer-safety/*"
          element={protectRoute(
            "/farmer-safety",
            <SuspensedView>
              <FarmerSafetyRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/team-consultant/*"
          element={protectRoute(
            "/team-consultant",
            <SuspensedView>
              <TeamConsultantRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/generic-group/*"
          element={protectRoute(
            "/generic-group",
            <SuspensedView>
              <GenericGroupRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/products/*"
          element={protectRoute(
            "/products",
            <SuspensedView>
              <ProductRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/pending-products/*"
          element={protectRoute(
            "/pending-products",
            <SuspensedView>
              <PendingProductsRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/product-category/*"
          element={protectRoute(
            "/product-category",
            <SuspensedView>
              <ProductCategoryRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/product-sub-category/*"
          element={protectRoute(
            "/product-sub-category",
            <SuspensedView>
              <ProductSubCategoryRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/product-company/*"
          element={protectRoute(
            "/product-company",
            <SuspensedView>
              <ProductCompanyRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/product-element/*"
          element={protectRoute(
            "/product-element",
            <SuspensedView>
              <ProductElementRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/product-solution/*"
          element={protectRoute(
            "/product-solution",
            <SuspensedView>
              <ProductSolutionRootPages />
            </SuspensedView>,
          )}
        />

        <Route
          path="/product-size/*"
          element={protectRoute(
            "/product-size",
            <SuspensedView>
              <ProductSizeRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/product-unit-type/*"
          element={protectRoute(
            "/product-unit-type",
            <SuspensedView>
              <ProductUnitRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/product-color-code/*"
          element={protectRoute(
            "/product-color-code",
            <SuspensedView>
              <ColorCodeRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/disease-insect/*"
          element={protectRoute(
            "/disease-insect",
            <SuspensedView>
              <DiseaseInsectRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/working-mood/*"
          element={protectRoute(
            "/working-mood",
            <SuspensedView>
              <WorkingMoodRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/warning-category/*"
          element={protectRoute(
            "/warning-category",
            <SuspensedView>
              <EarlyWarningCategoryRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/early-warning/*"
          element={protectRoute(
            "/early-warning",
            <SuspensedView>
              <EarlyWarningRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/order/*"
          element={protectRoute(
            "/order",
            <SuspensedView>
              <OrderRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/stock/*"
          element={protectRoute(
            "/stock",
            <SuspensedView>
              <StockRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/disease-detection/*"
          element={protectRoute(
            "/disease-detection",
            <SuspensedView>
              <DiseaseDetectionRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/course/*"
          element={protectRoute(
            "/course",
            <SuspensedView>
              <CourseRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/course-category/*"
          element={protectRoute(
            "/course-category",
            <SuspensedView>
              <CourseCategoryRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/dealer/*"
          element={protectRoute(
            "/dealer",
            <SuspensedView>
              <DealerRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/pending-dealer/*"
          element={protectRoute(
            "/pending-dealer",
            <SuspensedView>
              <PendingDealerRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/pending-payment-method/*"
          element={protectRoute(
            "/pending-payment-method",
            <SuspensedView>
              <PendingPaymentMethodRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/approved-payment-method/*"
          element={protectRoute(
            "/approved-payment-method",
            <SuspensedView>
              <ApprovedPaymentMethodRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/banner/*"
          element={protectRoute(
            "/banner",
            <SuspensedView>
              <BannerRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/feature/*"
          element={protectRoute(
            "/feature",
            <SuspensedView>
              <FeatureRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/chad-bagan-tips/*"
          element={protectRoute(
            "/chad-bagan-tips",
            <SuspensedView>
              <ChadBaganTipsRootPage />
            </SuspensedView>,
          )}
        />

        <Route
          path="/account-lead/*"
          element={protectRoute(
            "/chad-bagan-tips",
            <SuspensedView>
              <AccountLeadRootPage />
            </SuspensedView>,
          )}
        />

        {/* Page Not Found */}
        <Route path="*" element={<Navigate to="/error/404" />} />
      </Route>
    </Routes>
  );
};

const SuspensedView: FC<{ children: React.ReactNode }> = ({ children }) => {
  const baseColor = getCSSVariableValue("--bs-primary");
  TopBarProgress.config({
    barColors: {
      "0": baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  });
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};

export { PrivateRoutes };
